import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Gift, Star, Trash2, CheckCircle2, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const imgSrc = (url) => (!url ? null : url.startsWith('http') ? url : `${API_URL}${url}`);
const fmtDate = (d) => {
  try { return new Date(`${d}T00:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }); }
  catch { return d; }
};

/**
 * Lets a customer spend loyalty points on a catalog add-on, applied free to an
 * upcoming active booking. Reused as an inline section (customer Loyalty page)
 * and inside a dialog locked to one booking (My Bookings card).
 */
export const AddonRedeemPanel = ({ lockedBookingId = null, variant = 'section', onChanged }) => {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(lockedBookingId || '');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/loyalty/redeemable-addons`, { withCredentials: true });
      setCtx(res.data);
      if (!lockedBookingId) {
        setSelectedBooking((prev) => prev || res.data?.eligible_bookings?.[0]?.booking_id || '');
      }
    } catch (e) {
      toast.error('Failed to load add-on rewards');
    } finally { setLoading(false); }
  }, [lockedBookingId]);

  useEffect(() => { load(); }, [load]);

  const activeBookingId = lockedBookingId || selectedBooking;
  const balance = ctx?.balance || 0;
  const eligible = ctx?.eligible_bookings || [];
  const addons = ctx?.addons || [];
  const applied = (ctx?.my_redemptions || []).filter(
    (r) => !activeBookingId || r.booking_id === activeBookingId,
  );

  const redeem = async (addon) => {
    if (!activeBookingId) { toast.error('Pick a booking to apply this reward to'); return; }
    setBusyId(addon.reward_id);
    try {
      await axios.post(`${API_URL}/api/customer/loyalty/redeem-addon`,
        { reward_id: addon.reward_id, booking_id: activeBookingId }, { withCredentials: true });
      toast.success(`${addon.name} added to your party — free!`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply reward');
    } finally { setBusyId(null); }
  };

  const remove = async (red) => {
    if (!window.confirm(`Remove "${red.addon_name}" and refund ${red.points_cost} points?`)) return;
    setBusyId(red.redemption_id);
    try {
      await axios.delete(`${API_URL}/api/customer/loyalty/redeem-addon/${red.redemption_id}`, { withCredentials: true });
      toast.success(`Removed — ${red.points_cost} points refunded`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove reward');
    } finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="addon-panel-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B6B]" />
      </div>
    );
  }

  return (
    <div data-testid="addon-redeem-panel">
      {/* Balance + booking picker */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF4E6] border border-[#FFD9A8] px-3 py-1.5 text-sm font-semibold text-[#B8860B]" data-testid="addon-panel-balance">
          <Star className="w-4 h-4 text-[#E8A80E]" /> {balance.toLocaleString()} pts available
        </span>
        {!lockedBookingId && eligible.length > 0 && (
          <div className="min-w-[220px]">
            <Select value={selectedBooking} onValueChange={setSelectedBooking}>
              <SelectTrigger className="rounded-full border-[#E8E5DF] h-10" data-testid="addon-booking-select">
                <SelectValue placeholder="Choose a booking…" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {eligible.map((b) => (
                  <SelectItem key={b.booking_id} value={b.booking_id} data-testid={`addon-booking-opt-${b.booking_id}`}>
                    {(b.child_name || 'Party')}'s party · {fmtDate(b.date)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {eligible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E8E5DF] bg-[#FFFDF8] p-6 text-center" data-testid="addon-no-bookings">
          <PartyPopper className="w-8 h-8 text-[#FF3B6B] mx-auto mb-2" />
          <p className="text-sm text-[#6B6B7A]">
            You have no upcoming parties to add rewards to yet. Book a party and your points can unlock free add-ons.
          </p>
        </div>
      ) : (
        <>
          {/* Already applied to this booking */}
          {applied.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="addon-applied-list">
              {applied.map((r) => (
                <div key={r.redemption_id} className="flex items-center justify-between gap-3 rounded-xl border border-[#06D6A0]/30 bg-[#06D6A0]/5 px-3 py-2.5" data-testid={`applied-redemption-${r.redemption_id}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#058B69] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1E1E24] truncate">{r.addon_name} <span className="text-[#058B69] font-normal">— free</span></p>
                      {!lockedBookingId && (
                        <p className="text-[11px] text-[#6B6B7A] truncate">on {(r.child_name || 'party')}'s party · {fmtDate(r.booking_date)}</p>
                      )}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)} disabled={busyId === r.redemption_id}
                    className="rounded-full text-[#FF3B6B] hover:bg-[#FFE9EE] h-8" data-testid={`applied-remove-${r.redemption_id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> {busyId === r.redemption_id ? 'Removing…' : 'Remove'}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Redeemable add-on grid */}
          {addons.length === 0 ? (
            <p className="text-sm text-[#6B6B7A] italic">No add-ons available to redeem right now.</p>
          ) : (
            <div className={`grid gap-3 ${variant === 'dialog' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
              {addons.map((a) => {
                const src = imgSrc(a.image_url);
                const afford = a.affordable;
                return (
                  <div key={a.reward_id} className={`rounded-2xl border bg-white overflow-hidden flex flex-col ${afford ? 'border-[#E8E5DF]' : 'border-[#E8E5DF] opacity-75'}`} data-testid={`addon-card-${a.reward_id}`}>
                    <div className="h-24 bg-[#FFF4E6] flex items-center justify-center overflow-hidden">
                      {src ? (
                        <img src={src} alt={a.name} className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <Gift className="w-8 h-8 text-[#FB923C]" />
                      )}
                    </div>
                    <div className="p-3 flex flex-col flex-1">
                      <p className="text-sm font-semibold text-[#1E1E24] leading-tight">{a.name}</p>
                      <div className="mt-1 flex items-center gap-2 text-[11px]">
                        <span className="inline-flex items-center gap-1 font-semibold text-[#B8860B]"><Star className="w-3 h-3 text-[#E8A80E]" /> {a.points_cost.toLocaleString()} pts</span>
                        {a.dollar_value > 0 && <span className="text-[#6B6B7A]">${a.dollar_value.toFixed(2)} value</span>}
                      </div>
                      <Button
                        onClick={() => redeem(a)}
                        disabled={!afford || !activeBookingId || busyId === a.reward_id}
                        className={`mt-3 w-full rounded-full h-9 text-sm ${afford ? 'bg-[#FF3B6B] hover:bg-[#E62E58] text-white' : 'bg-gray-100 text-[#9a9aa6]'}`}
                        data-testid={`addon-redeem-${a.reward_id}`}
                      >
                        {busyId === a.reward_id ? 'Applying…' : afford ? 'Redeem free' : `Need ${a.deficit.toLocaleString()} more`}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddonRedeemPanel;
